import React, { FormEvent } from "react";
import { Button, Input, Textarea, Tooltip } from "@heroui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { State, ValidationErrors } from "../../types/types";
import { TFunction } from "react-i18next";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

const labelClass =
  "!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0 after:hidden";

const inputClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const textareaClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

interface VideoUploadStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: State;
  validationErrors: ValidationErrors;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeVideo: () => void;
  addYouTubeLink: () => void;
  updateYouTubeLink: (
    index: number,
    field: "ytLink" | "shortTitle" | "shortDescription",
    value: string,
  ) => void;
  removeYouTubeLink: (index: number) => void;
  isValidURL: (url: string) => boolean;
  handleNext: (
    e: FormEvent<HTMLFormElement>,
    nextStage: State["stage"],
  ) => void;
  handleBack: () => void;
}

const VideoUploadStage: React.FC<VideoUploadStageProps> = ({
  t,
  state,
  validationErrors,
  handleFileChange,
  removeVideo,
  addYouTubeLink,
  updateYouTubeLink,
  removeYouTubeLink,
  isValidURL,
  handleNext,
  handleBack,
}) => {
  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                <span className="inline-flex items-center gap-2">
                  {VIDEO_UPLOAD_ENABLED
                    ? t("videoUpload.title")
                    : t("videoUpload.youtubeOnlyTitle")}
                  <Tooltip
                    className="w-[300px]"
                    placement="right"
                    content={
                      <div className="text-xs p-1">
                        <span className="font-semibold block mb-0.5">
                          {t("keyVideoTitle")}
                        </span>
                        <span>{t("keyVideoDescription")}</span>
                      </div>
                    }
                  >
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Tooltip>
                </span>
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {VIDEO_UPLOAD_ENABLED
                  ? t("videoUpload.subtitle")
                  : t("videoUpload.youtubeOnlySubtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={(e) => handleNext(e, "sponsorStage")}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
                {VIDEO_UPLOAD_ENABLED && (
                  <>
                    {state.formData.videoFile ? (
                      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 p-3 bg-slate-50">
                        <video
                          src={URL.createObjectURL(state.formData.videoFile)}
                          controls
                          className="w-full h-36 object-cover rounded-xl"
                          onError={(e) => {
                            console.error("Video load error:", e);
                          }}
                        />
                        <p className="text-xs text-[#71717A] truncate">
                          {state.formData.videoFile.name}
                        </p>
                        <Button
                          onPress={removeVideo}
                          color="danger"
                          variant="flat"
                          size="sm"
                          className="self-end rounded-xl bg-red-50 text-red-500 hover:bg-red-100 text-xs font-medium px-3 py-1.5 transition-all"
                        >
                          {t("videoUpload.removeVideo")}
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-slate-200 lg:border-[#EBEBEC] bg-slate-50 lg:bg-[#EBEBEC]/40 hover:bg-slate-100 h-32 flex items-center justify-center rounded-2xl transition-all">
                        <label
                          htmlFor="videoFile"
                          className="cursor-pointer flex flex-col items-center p-4 text-center"
                        >
                          <svg
                            className="w-8 h-8 text-slate-400 mb-1.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.8}
                              d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                            />
                          </svg>
                          <span className="text-xs sm:text-sm font-semibold text-[#18181B]">
                            {t("videoUpload.uploadLabel")}
                          </span>
                          <span className="text-[11px] sm:text-xs text-[#71717A] mt-0.5">
                            {t("videoUpload.uploadDescription")}
                          </span>
                          <input
                            id="videoFile"
                            name="videoFile"
                            type="file"
                            accept="video/mp4,video/mov"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}

                    {validationErrors["videoFile"] && (
                      <p className="text-red-500 text-xs text-left ml-1">
                        {validationErrors["videoFile"]}
                      </p>
                    )}
                  </>
                )}

                {/* YouTube Links Section */}
                <div className="space-y-3 pt-2">
                  <h3 className="text-xs sm:text-sm font-semibold lg:font-medium text-slate-700 lg:text-[#18181B] flex items-center gap-1.5">
                    <span>{t("videoUpload.youTubeLinks")}</span>
                    <Tooltip
                      className="w-[280px]"
                      placement="right"
                      content={
                        <div className="text-xs p-1">
                          <span className="font-semibold block mb-0.5">
                            {t("youtubeLinksTitle")}
                          </span>
                          <span>{t("youtubeLinksDescription")}</span>
                        </div>
                      }
                    >
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                    </Tooltip>
                  </h3>

                  {state.formData.fighterDto?.ytLinkDetailsDto?.map((link, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-slate-50 lg:bg-[#F9FAFB] p-3.5"
                    >
                      <Input
                        type="url"
                        name={`youtubeLink-${index}`}
                        label={t("videoUpload.youTubeLink")}
                        labelPlacement="outside"
                        placeholder={t("videoUpload.youTubeLinkPlaceholder")}
                        value={link.ytLink}
                        onValueChange={(value) =>
                          updateYouTubeLink(index, "ytLink", value)
                        }
                        isInvalid={
                          !!validationErrors[`ytLinkDetailsDto.${index}.youtubeLink`]
                        }
                        errorMessage={
                          validationErrors[`ytLinkDetailsDto.${index}.youtubeLink`]
                        }
                        className="w-full"
                        classNames={inputClassNames}
                        startContent={
                          <GlobeAltIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                        }
                      />
                      <Input
                        type="text"
                        name={`shortTitle-${index}`}
                        label={t("videoUpload.shortTitle")}
                        maxLength={25}
                        labelPlacement="outside"
                        placeholder={t("videoUpload.shortTitlePlaceholder")}
                        value={link.shortTitle}
                        onValueChange={(value) =>
                          updateYouTubeLink(index, "shortTitle", value)
                        }
                        className="w-full"
                        classNames={inputClassNames}
                        startContent={
                          <UserIcon className="w-4 h-4 text-gray-400 pointer-events-none flex-shrink-0" />
                        }
                      />
                      <Textarea
                        name={`shortDescription-${index}`}
                        label={t("videoUpload.shortDescription")}
                        labelPlacement="outside"
                        placeholder={t("videoUpload.shortDescriptionPlaceholder")}
                        value={link.shortDescription}
                        onValueChange={(value) =>
                          updateYouTubeLink(index, "shortDescription", value)
                        }
                        maxLength={200}
                        isInvalid={
                          !!validationErrors[
                            `ytLinkDetailsDto.${index}.shortDescription`
                          ]
                        }
                        errorMessage={
                          validationErrors[
                            `ytLinkDetailsDto.${index}.shortDescription`
                          ]
                        }
                        className="w-full"
                        classNames={textareaClassNames}
                      />
                      <Button
                        onPress={() => removeYouTubeLink(index)}
                        color="danger"
                        variant="flat"
                        size="sm"
                        className="self-end rounded-xl bg-red-50 text-red-500 hover:bg-red-100 text-xs font-medium px-3 py-1.5 transition-all"
                      >
                        {t("videoUpload.removeLink")}
                      </Button>
                    </div>
                  ))}

                  <Button
                    onPress={addYouTubeLink}
                    variant="bordered"
                    className="w-full rounded-xl border border-[#0485F7] text-[#0485F7] text-xs sm:text-sm font-semibold h-9 hover:bg-[#0485F7]/10 transition-all"
                  >
                    {t("videoUpload.addLink")}
                  </Button>
                </div>
              </div>

              {/* Footer: Back / Next */}
              <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
                <Button
                  onPress={handleBack}
                  variant="flat"
                  startContent={<ArrowLeftIcon className="h-4 w-4" />}
                  className={tertiaryButtonClass}
                >
                  {t("navigation.back")}
                </Button>

                <Button
                  type="submit"
                  endContent={<ArrowRightIcon className="h-4 w-4" />}
                  className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm"
                >
                  {t("navigation.next")}
                </Button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
            {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
          </p>
        </div>
      </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default VideoUploadStage;
